/**
 * Annex II (AQ) draft export client (#52-d).
 *
 * Like the IAA export, the response is a binary .docx — raw same-origin
 * fetch (credentials included), returning a Blob + the server-named file.
 * No party picker: the server uses the agreement's latest saved assessment
 * and its HOLDER parties. The structured 409 ("aq_no_assessment") means
 * there is no saved adequacy assessment to render yet.
 */
import { downloadBlob } from './iaaExports';
import type { DocumentLanguage, ISODate, UUID } from './types';

export { downloadBlob };

export interface AqExportRequestBody {
  language?: DocumentLanguage;
  document_date?: ISODate;
  response_id?: UUID;
}

export interface AqExportResult {
  blob: Blob;
  fileName: string;
  exportId: string | null;
}

/** Raised on the structured 409 when the agreement has no saved assessment. */
export class AqNoAssessmentError extends Error {}

export async function exportAqDraft(
  agreementId: UUID,
  body: AqExportRequestBody = {},
): Promise<AqExportResult> {
  const res = await fetch(`/agreements/${agreementId}/aq-exports`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail: unknown = null;
    try { detail = (await res.json()).detail; } catch { /* non-JSON error body */ }
    if (
      res.status === 409 && detail && typeof detail === 'object'
      && (detail as { code?: string }).code === 'aq_no_assessment'
    ) {
      throw new AqNoAssessmentError((detail as { message: string }).message);
    }
    const msg = typeof detail === 'string'
      ? detail
      : 'The Annex II draft could not be generated. Please try again.';
    throw new Error(msg);
  }
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  const fileName = m ? decodeURIComponent(m[1]) : 'AnexoII_AQ_DRAFT.docx';
  return {
    blob: await res.blob(),
    fileName,
    exportId: res.headers.get('X-Aq-Export-Id'),
  };
}
